import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, Mail, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiWorker } from '../../api/worker';
import { TOOL_EVENTS, track } from '../../lib/analytics';
import type { ToolCtaCopy } from '../../lib/toolCopy';

interface ToolExportFollowUpProps {
  toolTitle: string;
  toolSlug: string;
  copy: ToolCtaCopy;
  onDismiss: () => void;
}

/**
 * Optional, post-export email capture.
 *
 * Export is never gated. This panel only appears after the user already has
 * their file, and marketing consent is unchecked by default.
 */
export function ToolExportFollowUp({
  toolTitle,
  toolSlug,
  copy,
  onDismiss,
}: ToolExportFollowUpProps) {
  const [email, setEmail] = useState(() => {
    try {
      return localStorage.getItem('orgsets_lead_email') || '';
    } catch {
      return '';
    }
  });
  const [optIn, setOptIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.includes('@')) {
      toast.error('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    const trimmedEmail = email.trim().toLowerCase();

    try {
      await apiWorker.post('/api/public/contact', {
        firstName: 'Tool user',
        lastName: toolTitle,
        email: trimmedEmail,
        type: 'TOOL_EXPORT_FOLLOWUP',
        message: [
          `Post-export follow-up requested for ${toolTitle} (/tools/${toolSlug}).`,
          '',
          `• Email: ${trimmedEmail}`,
          `• Toolbox updates opt-in: ${optIn ? 'Yes' : 'No'}`,
          `• Segment: ${copy.segment}`,
          `• Timestamp: ${new Date().toISOString()}`,
        ].join('\n'),
      });
    } catch {
      // The export already happened; never block the user on a lead call.
    } finally {
      try {
        localStorage.setItem('orgsets_lead_email', trimmedEmail);
      } catch {
        // Ignore storage failures.
      }

      track(TOOL_EVENTS.exportFollowUpSubmit, {
        tool_slug: toolSlug,
        tool_segment: copy.segment,
        marketing_opt_in: optIn,
      });

      setIsSubmitting(false);
      setIsDone(true);
      toast.success('Request received. Check your inbox shortly.');
    }
  };

  return (
    <div className="mt-6 border-[4px] border-black dark:border-white bg-[#FFD600] text-black shadow-[8px_8px_0px_#000] p-5 md:p-6 print:hidden">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 shrink-0 bg-black text-[#FFD600] border-2 border-black flex items-center justify-center">
            <Mail size={18} />
          </div>
          <div>
            <p className="text-[10px] font-mono font-black uppercase tracking-widest text-black/60">
              Your export is ready
            </p>
            <h3 className="text-lg font-black font-display uppercase tracking-tight leading-none mt-1">
              {copy.followUpHeadline}
            </h3>
            <p className="text-xs font-bold mt-2 max-w-xl leading-relaxed text-black/75">
              {copy.followUpBody}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="p-1.5 border-2 border-black bg-white hover:bg-black hover:text-white transition-colors cursor-pointer"
          aria-label="Dismiss email offer"
        >
          <X size={16} strokeWidth={3} />
        </button>
      </div>

      {isDone ? (
        <div className="mt-5 flex items-center gap-2 border-t-2 border-black/20 pt-4 text-sm font-bold">
          <CheckCircle2 size={18} className="text-emerald-700" />
          We saved your request. You can keep using the tool.
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-5 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@organization.org"
              aria-label="Email address"
              className="flex-1 border-[3px] border-black bg-white px-3 py-2.5 font-mono text-sm font-bold text-black placeholder:text-black/40 focus:outline-none focus:border-[#0044FF]"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-black text-white border-[3px] border-black font-black uppercase tracking-wider px-5 py-2.5 text-xs hover:bg-[#0044FF] transition-colors disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer"
            >
              {isSubmitting ? 'Sending...' : 'Email me a copy'}
              <ArrowRight size={14} strokeWidth={3} />
            </button>
          </div>
          <label className="flex items-start gap-2 text-[11px] font-bold text-black/70 cursor-pointer">
            <input
              type="checkbox"
              checked={optIn}
              onChange={(event) => setOptIn(event.target.checked)}
              className="w-4 h-4 mt-0.5 border-2 border-black accent-black"
            />
            <span>Send me occasional OrgSets tips and new free tools. Unsubscribe anytime.</span>
          </label>
          <p className="text-[10px] font-mono font-bold uppercase tracking-wider text-black/50">
            No account required. Your export has already been delivered.
          </p>
        </form>
      )}
    </div>
  );
}
